import * as types from 'src/store/mutation-types'
import { InvoiceClient } from 'src/api/Invoices'
import { Notify } from 'quasar'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'
import store from 'src/store'

// initial state
// shape: [{ id, quantity }]
const state = {
  invoices: [],
  invoicesMeta: null,
  invoicesLoaded: false
}

// getters
const getters = {
  invoices: state => state.invoices,
  invoicesMeta: state => state.invoicesMeta,
  invoicesLoaded: state => state.invoicesLoaded
}

const paginationDefault = {
  page: 1,
  rowsPerPage: 15,
  descending: true,
  sortBy: 'created_at',
  additionalIncludes: []
}

const actions = {
  listInvoices ({ commit, state }, { pagination } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_INVOICES_LOADED, false)

    return new InvoiceClient()
      .include(['customer', ...pagination.additionalIncludes])
      .filter(pagination.filterBy || [])
      .sort(sort)
      .limit(pagination.rowsPerPage)
      .page(pagination.page)
      .list()
      .then(response => {
        commit(types.SET_INVOICES, response.data.data)
        commit(types.SET_INVOICES_META, response.data.meta)
        commit(types.SET_INVOICES_LOADED, true)
      })
      .then(() => Promise.resolve({ invoices: state.invoices, meta: state.invoicesMeta }))
  },

  fetchNextInvoicesChunk ({ commit, state }, { done, pagination = {}, type = [] }) {
    if (!state.invoicesLoaded) {
      return done()
    } else if (!state.invoicesMeta) {
      return done(true)
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    let currentPage = state.invoicesMeta.pagination.current_page
    let totalPages = state.invoicesMeta.pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      return new InvoiceClient()
        .include(['customer', ...pagination.additionalIncludes])
        .filter(pagination.filterBy || [])
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)
        .list()
        .then(response => {
          let newInvoices = state.invoices.concat(response.data.data)

          commit(types.SET_INVOICES, newInvoices)
          commit(types.SET_INVOICES_META, response.data.meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  refreshInvoice ({ commit, state }, { invoiceId, additionalIncludes = [] }) {
    new InvoiceClient()
      .include(['customer', ...additionalIncludes])
      .get(invoiceId)
      .then(response => {
        const invoice = response.data.data[0]

        commit(types.UPDATE_INVOICE, invoice)
      })
  },

  addInvoice ({ commit, state }, invoice) {
    commit(types.ADD_INVOICE, invoice)
  },

  searchInvoices ({ commit, state }, { pagination, filter }) {
    commit(types.SET_INVOICES_LOADED, false)

    return new InvoiceClient()
      .include(['customer'])
      .search(filter)
      .then(response => {
        commit(types.SET_INVOICES, response.data.data)
        commit(types.SET_INVOICES_META, {
          pagination: {
            count: response.data.data.length,
            current_page: pagination.page,
            per_page: pagination.rowsPerPage,
            total: response.data.data.length,
            total_pages: 1
          }
        })
        commit(types.SET_INVOICES_LOADED, true)
      })
      .then(() => Promise.resolve({ invoices: state.invoices, meta: state.invoicesMeta }))
  },

  deleteInvoices ({ commit, state }, invoices) {
    let ids = invoices.map(invoice => invoice.id)

    return new InvoiceClient()
      .destroy(ids)
      .then(() => invoices.forEach(invoice => commit(types.REMOVE_INVOICE, invoice)))
      .then(() => commit(types.SET_INVOICES_META, {
        pagination: {
          ...state.invoicesMeta.pagination,
          total: state.invoicesMeta.pagination.total - invoices.length
        }
      }))
      .then(() => Notify.create({ message: i18n.tc('invoice_deleted', 2), type: 'positive' }))
      .then(() => Promise.resolve({ invoices: state.invoices, meta: state.invoicesMeta }))
      .catch(createErrorFromResponse)
  },

  setInvoiceStatus ({ commit }, { invoiceId, status }) {
    return new InvoiceClient()
      .include(['customer'])
      .setStatus(invoiceId, { 'status_code': status })
      .then(response => {
        commit(types.UPDATE_INVOICE, response.data.data)
        return response.data.data
      })
  },

  addInvoicePayment ({ commit }, { invoiceId, balance = null }) {
    let payment
    if (!balance) {
      payment = { }
    } else {
      payment = { balance }
    }
    return new InvoiceClient()
      .include(['customer'])
      .addPayment(invoiceId, payment)
      .then(response => {
        commit(types.UPDATE_INVOICE, response.data.data)
      })
  },

  deleteInvoicePayments ({ commit, dispatch }, { invoiceId, payments }) {
    return Promise.all(payments.map(payment => {
      return new InvoiceClient()
        .destroyPayment(invoiceId, payment)
    }))
      .then(() => {
        dispatch('refreshInvoice', { invoiceId })
      })
  },

  commitInvoice ({ commit }, invoiceId) {
    return new InvoiceClient()
      .include(['customer'])
      .commit(invoiceId)
      .then(response => {
        commit(types.UPDATE_INVOICE, response.data.data)
        return response.data.data
      })
  }
}

// mutations
const mutations = {
  [types.SET_INVOICES] (state, invoices) {
    state.invoices = invoices
  },

  [types.ADD_INVOICE] (state, invoice) {
    state.invoices.unshift(invoice)
    // ToDo: Pagnation now may be broken
  },

  [types.SET_INVOICES_META] (state, invoicesMeta) {
    state.invoicesMeta = invoicesMeta
  },

  [types.SET_INVOICES_LOADED] (state, loaded) {
    state.invoicesLoaded = loaded
  },

  [types.REMOVE_INVOICE] (state, invoice) {
    const toDelete = state.invoices.find(stateInvoice => stateInvoice.id === invoice.id)
    const index = state.invoices.indexOf(toDelete)

    if (index !== -1) {
      state.invoices.splice(index, 1)
    }
  },

  [types.UPDATE_INVOICE] (state, updatedInvoice) {
    let invoiceIndex = state.invoices.findIndex(invoice => invoice.id === updatedInvoice.id)
    state.invoices.splice(invoiceIndex, 1, updatedInvoice)

    const selectedCustomer = store.getters.selectedCustomer

    if (selectedCustomer && selectedCustomer.id === updatedInvoice.customer_id && selectedCustomer.invoices) {
      let customerInvoiceIndex = selectedCustomer.invoices.data.findIndex(invoice => invoice.id === updatedInvoice.id)

      if (customerInvoiceIndex !== -1) {
        selectedCustomer.invoices.data.splice(customerInvoiceIndex, 1, updatedInvoice)

        store.commit(types.UPDATE_CUSTOMER, selectedCustomer)
      }
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
